import {
  type PropsWithChildren,
  type ReactElement,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom-v5-compat";
import { GetSessionInfoResponseEnum } from "@b2bportal/auth-api";
import { getWebViewInfo } from "@b2bportal/core-utilities";
import type { RbcPartnerMetadata } from "@b2bportal/rbc-accounts-api";
import { setDatadogUser } from "@hopper-b2b/datadog";
import { B2B_SESSION_ID_KEY, setLang, useI18nContext } from "@hopper-b2b/i18n";
import { updateSession } from "@hopper-b2b/self-serve";
import { B2B_PORTAL_AUTH_REDIRECT_TO } from "@hopper-b2b/types";
import { PATH_AUTH_INVALID_SESSION } from "@hopper-b2b/utilities";
import { UserContext } from "../../../App";
import getSession from "@/api/session/getSession";
import { postToNativeBridge } from "@/utils/nativeBridge";

const ProtectedRoute = (props: PropsWithChildren): ReactElement | null => {
  const { children } = props;
  const [authenticated, setAuthenticated] = useState(false);
  const hasPublishedLoggedIn = useRef(false);
  const { brand, language } = useI18nContext();
  const { sessionInfo, updateSessionInfo, setError } = useContext(UserContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    const userInfoCall = async () => {
      try {
        const response = await getSession();
        switch (response.GetSessionInfoResponse) {
          case GetSessionInfoResponseEnum.Session: {
            updateSessionInfo?.(response.sessionInfo);
            // Mirror the session into the self-serve redux slice so agent
            // impersonation is detected on cold starts (new tab / full reload).
            // `delegatedTo` was previously only set via the /impersonation route
            // and held in-memory, so funnels that boot a fresh document (hotels,
            // ground) lost it — causing `is_agent_session` to be false on their
            // tracking events. The AxiosInterceptor reads `delegatedTo` from this
            // slice via `getDelegatedTo`.
            dispatch(updateSession(response.sessionInfo));
            setAuthenticated(true);
            const rbcData = response?.sessionInfo?.userInfo
              ?.partnerMetadata as RbcPartnerMetadata;
            // Detect new login sessions by tracking sessionId changes.
            // This ensures that device language changes are picked up between sessions while
            // preserving manual language toggles within the same session.
            // The stored value is cleared on logout (EndSessionComponent).
            const storedSessionId = localStorage.getItem(B2B_SESSION_ID_KEY);
            const currentSessionId = rbcData?.sessionId;
            const isNewSession =
              currentSessionId != null && storedSessionId !== currentSessionId;

            // Apply backend language preference only on new login sessions
            if (
              isNewSession &&
              rbcData?.preferredLanguage &&
              brand?.supportedLanguages
                ?.map((l) => l.key)
                .includes(rbcData.preferredLanguage) &&
              language !== rbcData.preferredLanguage
            ) {
              setLang(rbcData.preferredLanguage);

              window.location.reload();
              return;
            }

            // Store session ID AFTER language preference is handled to avoid race conditions.
            // Only set when we have a valid session ID (never remove here - logout handles clearing).
            if (isNewSession && currentSessionId) {
              localStorage.setItem(B2B_SESSION_ID_KEY, currentSessionId);
            }
            break;
          }
          default: {
            // Session was rejected, navigate to invalid session page
            setAuthenticated(false);
            const redirectUrl = `${window.location.pathname}${window.location.search}`;
            if (!redirectUrl.includes("/auth/")) {
              sessionStorage.setItem(B2B_PORTAL_AUTH_REDIRECT_TO, redirectUrl);
            }
            navigate(PATH_AUTH_INVALID_SESSION);
            break;
          }
        }
      } catch (err) {
        setError?.(err?.toString());
        const redirectUrl = `${window.location.pathname}${window.location.search}`;
        if (!redirectUrl.includes("/auth/")) {
          sessionStorage.setItem(B2B_PORTAL_AUTH_REDIRECT_TO, redirectUrl);
        }
        // TODO: before deploying to prod, this should go back to avion app
        navigate(PATH_AUTH_INVALID_SESSION);
      }
    };
    // Skip getSession call if sessionInfo is already populated (e.g., from AuthModule)
    if (!authenticated && !sessionInfo?.userScope?.isSignedIn) {
      userInfoCall();
    } else if (sessionInfo?.userScope?.isSignedIn) {
      setAuthenticated(true);
    }
  }, [
    authenticated,
    sessionInfo,
    navigate,
    setError,
    updateSessionInfo,
    dispatch,
    brand,
  ]);

  useEffect(() => {
    if (authenticated && sessionInfo?.userScope.isSignedIn) {
      const rbcData = sessionInfo?.userInfo?.partnerMetadata as
        | RbcPartnerMetadata
        | undefined;

      setDatadogUser({
        id: rbcData?.hopperUserId,
      });

      if (!hasPublishedLoggedIn.current) {
        hasPublishedLoggedIn.current = true;

        const webviewInfo = getWebViewInfo();

        if (typeof window !== "undefined" && window.dataLayer) {
          window.dataLayer.push({
            lob: "avion-web",
            channel: "secure",
            content_group: "avion-web : hopper",
            loyalty_surrogate_id: rbcData?.tenantUserId,
            event: "logged_in",
            ...(webviewInfo.isWebView && {
              is_webview: webviewInfo.isWebView,
              webview_platform: webviewInfo.platform,
            }),
          });
        }

        if (webviewInfo.isWebView) {
          postToNativeBridge("logged_in", language, {
            loyalty_surrogate_id: rbcData?.tenantUserId,
          });
        }
      }
    }
  }, [authenticated, sessionInfo, language]);

  return authenticated ? <>{children}</> : null;
};

export default ProtectedRoute;
